/**
 * The admin shell, from `kaynak/screens.html`.
 *
 * The markup is the reference's, element for element: `.app` grid, the 236px `.sidebar` with its
 * brand block and inline nav icons, the `.nav-label` section heading, the `.sidebar-foot` account
 * block, the mobile `.topbar` with its `select`, and `main.main`. The nav icons are the
 * reference's own inline SVG paths — an icon font or a different icon set would move a pixel in
 * every navigation row on every screen.
 *
 * The product's three extra screens (Kişiler, Denetim Kaydı, Kullanıcılar) are deliberately NOT
 * in this navigation. They are real and they are kept — the demo reaches them from its own «Ek
 * ürün ekranları» section — but adding them here would lengthen the sidebar by three rows, move
 * everything below them, and make the shell comparison meaningless for a reason that has nothing
 * to do with the design.
 */
import type { ReactElement, ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useV07Account } from '../data/session';
import { useScrollRegionsFocusable } from '../lib/use-scroll-regions-focusable';

interface NavEntry {
  to: string;
  label: string;
  icon: ReactElement;
  /** Paths that should light this entry up, beyond the entry's own. */
  match?: string[];
}

const svg = (children: ReactNode): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    {children}
  </svg>
);

const NAV: NavEntry[] = [
  {
    to: '/admin',
    label: 'Panel',
    icon: svg(
      <>
        <rect x="3" y="3" width="8" height="8" />
        <rect x="13" y="3" width="8" height="8" />
        <rect x="3" y="13" width="8" height="8" />
        <rect x="13" y="13" width="8" height="8" />
      </>,
    ),
  },
  {
    to: '/admin/assets',
    label: 'Görsel Kütüphanesi',
    icon: svg(
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="8.5" cy="9.5" r="1.8" />
        <path d="M4 17l5-5 4 4 3-2 4 4" />
      </>,
    ),
  },
  {
    to: '/admin/templates',
    label: 'Şablonlar',
    icon: svg(
      <>
        <path d="M12 3l9 5-9 5-9-5 9-5z" />
        <path d="M3 13l9 5 9-5" />
      </>,
    ),
    match: ['/admin/templates'],
  },
  {
    to: '/admin/import',
    label: 'Toplu Üretim',
    icon: svg(
      <>
        <path d="M12 16V4" />
        <path d="M6 10l6-6 6 6" />
        <path d="M4 20h16" />
      </>,
    ),
  },
  {
    to: '/admin/credentials',
    label: 'Rozet ve Sertifikalar',
    icon: svg(
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M7 10h6M7 14h10" />
      </>,
    ),
    match: ['/admin/credentials'],
  },
];

const SETTINGS_ENTRY: NavEntry = {
  to: '/admin/settings/email',
  label: 'E-posta ve Doğrulama',
  icon: svg(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>,
  ),
  match: ['/admin/settings'],
};

/** The reference's own mobile menu options, in its own order. */
const MOBILE_OPTIONS: { value: string; label: string }[] = [
  { value: '/admin', label: 'Panel' },
  { value: '/admin/templates', label: 'Şablonlar' },
  { value: '/admin/import', label: 'Toplu Üretim' },
  { value: '/admin/assets', label: 'Görsel Kütüphanesi' },
  { value: '/admin/credentials', label: 'Rozet ve Sertifikalar' },
  { value: '/admin/settings/email', label: 'Ayarlar' },
];

function Brand(): ReactElement {
  return (
    <NavLink className="brand" to="/admin">
      <span className="mark" />
      <span className="txt">
        <b>HPW Credentials</b>
        <span className="sub">Yönetim</span>
      </span>
    </NavLink>
  );
}

/**
 * Which option the mobile select shows — `m10-router.js:85`, ported as written.
 *
 * The reference maps by SCREEN, not by path prefix, and the two disagree in one place that a
 * prefix rule gets wrong: every settings screen selects «Ayarlar», including
 * `/admin/settings/verification`, which shares no prefix with `/admin/settings/email`. A
 * longest-prefix rule falls back to «Panel» there — 323 pixels of difference at 390×844 and
 * 768×1024, and the wrong section named to a phone user.
 *
 * The rest of the table is the reference's too: the wizard and the editor both select
 * «Şablonlar», and a credential's detail selects «Rozet ve Sertifikalar».
 */
export function mobileValueFor(pathname: string): string {
  if (pathname.startsWith('/admin/settings')) return '/admin/settings/email';
  if (pathname.startsWith('/admin/templates')) return '/admin/templates';
  if (pathname.startsWith('/admin/credentials')) return '/admin/credentials';
  if (pathname.startsWith('/admin/import')) return '/admin/import';
  if (pathname.startsWith('/admin/assets')) return '/admin/assets';
  return '/admin';
}

function isActive(entry: NavEntry, pathname: string): boolean {
  if (entry.to === '/admin') return pathname === '/admin';
  for (const prefix of entry.match ?? [entry.to]) if (pathname.startsWith(prefix)) return true;
  return false;
}

export function AdminShell({
  children,
  topStrip = true,
}: {
  children: ReactNode;
  /**
   * Whether a navigation strip occupies the top of the viewport ABOVE this shell.
   *
   * The reference's whole shell arithmetic assumes one: `--barh` is the height of its own
   * `.proto-bar`, and `.app` reserves it (`min-height: calc(100vh - var(--barh))`) while `.sidebar`
   * sticks below it (`top: var(--barh)`). The parity demo renders its own `.demo-bar` in that exact
   * place, so it keeps the arithmetic and the default is `true`.
   *
   * The PRODUCT renders no strip at all. Leaving the reservation in place there is what put a 32px
   * gap above the sidebar — and only on routes tall enough to scroll, because a sticky offset does
   * nothing until the element would otherwise pass it. `false` removes the reservation; the styles
   * that do it live in `approved-corrections.css` against the attribute below.
   */
  topStrip?: boolean;
}): ReactElement {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const account = useV07Account();
  /* The prototype's own rule, for every screen this shell draws (`m01-utils.js`). */
  useScrollRegionsFocusable();

  const mobileValue = mobileValueFor(pathname);

  /* One control, whichever block below renders it: a real session drops the session, and the
     reference's own fallback is a link to the login screen. */
  const signOutControl = account?.signOut ? (
    /* A real sign-out is an act, not a link to a screen: it drops the session first. */
    <button
      type="button"
      className="small linklike"
      style={{ color: 'var(--text-3)' }}
      onClick={account.signOut}
    >
      Çıkış
    </button>
  ) : (
    <NavLink to="/admin/login" className="small" style={{ color: 'var(--text-3)' }}>
      Çıkış
    </NavLink>
  );

  const item = (entry: NavEntry): ReactElement => (
    <NavLink key={entry.to} to={entry.to} className={() => (isActive(entry, pathname) ? 'is-active' : '')}>
      {entry.icon}
      {entry.label}
    </NavLink>
  );

  return (
    <div className="app" {...(topStrip ? {} : { 'data-hpw-shell': 'standalone' })}>
      <aside className="sidebar">
        <Brand />
        <nav className="nav" id="sideNav" aria-label="Ana menü">
          {NAV.map(item)}
          <div className="nav-label">Ayarlar</div>
          {item(SETTINGS_ENTRY)}
        </nav>
        {/*
         * The account block, in two forms, and the reason there are two.
         *
         * Without an account this is the REFERENCE's own block, character for character — the
         * fallback name, «Yönetici · » with its middot, and a link. That is what the parity demo
         * renders and what the pixel gate measures, so it is left exactly as it was.
         *
         * With an account it is this product's, and it carries real values whose length nobody
         * chose: a display name can be a long one, and when it is absent the fallback is an
         * e-mail address, which in a 236px sidebar is longer than the space there is. So the
         * product's form marks its parts (`data-hpw-account*`, attributes no reference element
         * carries) and `approved-corrections.css` gives them a definite width to shrink inside and
         * a real gap between the role and the control. The middot goes with it: a separator and a
         * gap doing the same job leaves the dot orphaned.
         */}
        <div className="sidebar-foot">
          <span className="avatar">{account?.initials ?? 'TB'}</span>
          {account ? (
            <div data-hpw-account="">
              {/* The full value stays reachable when the visible one is cut short. */}
              <b title={account.name}>{account.name}</b>
              <span data-hpw-account-row="">
                <span data-hpw-account-role="">{account.roleLabel}</span>
                {signOutControl}
              </span>
            </div>
          ) : (
            <div>
              <b>Türker Baş</b>
              <span>Yönetici · </span>
              {signOutControl}
            </div>
          )}
        </div>
      </aside>

      <div style={{ minWidth: 0 }}>
        <div className="topbar">
          <Brand />
          <select
            className="select select-sm"
            id="mobileNav"
            aria-label="Menü"
            value={mobileValue}
            onChange={(event) => navigate(event.target.value)}
          >
            {MOBILE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <main className="main" id="adminMain">
          {children}
        </main>
      </div>
    </div>
  );
}
